import type {
  MessageEvent,
  MessageAnalysisResult,
  PassCriterion,
  SimulationRun,
  TestCase,
  TestCaseVerdict,
  TestVerdictEvidence,
  TestVerdictStatus,
} from '@k-ucap/shared';

const evaluateCriterion = (
  criterion: PassCriterion,
  _testCase: TestCase,
  run: SimulationRun,
  events: MessageEvent[],
  analyses: MessageAnalysisResult[]
): { status: TestVerdictStatus; evidence: TestVerdictEvidence[] } => {
  const evidence: TestVerdictEvidence[] = [];
  let status: TestVerdictStatus = 'PASS';

  const addFail = (issue: string) => {
    status = 'FAIL';
    evidence.push({ issue });
  };

  switch (criterion.type) {
    case 'NO_FAIL': {
      if (run.failCount > 0) {
        addFail(`${run.failCount} FAIL message(s) detected`);
      }
      break;
    }
    case 'MAX_WARN_COUNT': {
      const maxWarn = Number(criterion.params.maxWarn ?? 0);
      if (run.warnCount > maxWarn) {
        addFail(`WARN count ${run.warnCount} exceeds max ${maxWarn}`);
      }
      break;
    }
    case 'MIN_PASS_RATE': {
      const minRate = Number(criterion.params.minRate ?? 1);
      const rate = run.generatedCount > 0 ? run.passCount / run.generatedCount : 0;
      if (rate < minRate) {
        addFail(`PASS rate ${(rate * 100).toFixed(1)}% below ${(minRate * 100).toFixed(1)}%`);
      }
      break;
    }
    case 'EXPECTED_MESSAGE_COUNT': {
      const minCount = Number(criterion.params.minCount ?? 0);
      const maxCount = criterion.params.maxCount !== undefined ? Number(criterion.params.maxCount) : Infinity;
      if (run.generatedCount < minCount || run.generatedCount > maxCount) {
        addFail(
          `Generated count ${run.generatedCount} not in range [${minCount}, ${maxCount === Infinity ? '∞' : maxCount}]`
        );
      }
      break;
    }
    case 'EXPECTED_MESSAGE_TYPE': {
      const expectedType = Number(criterion.params.messageType);
      if (!run.messageStats[expectedType]) {
        addFail(`Message type ${expectedType} was not generated`);
      }
      break;
    }
    case 'FIELD_RANGE': {
      const field = String(criterion.params.field ?? '');
      const min = Number(criterion.params.min ?? -Infinity);
      const max = Number(criterion.params.max ?? Infinity);
      const failures = events.filter((e) => {
        const payload = (e.payload ?? e.fields ?? {}) as Record<string, unknown>;
        const value = payload[field];
        return typeof value !== 'number' || value < min || value > max;
      });
      if (failures.length > 0) {
        addFail(`Field ${field} out of range [${min}, ${max}] in ${failures.length} message(s)`);
      }
      break;
    }
    case 'ROLE_MATCH': {
      const expectedSource = criterion.params.source as string | undefined;
      const expectedTarget = criterion.params.target as string | undefined;
      const failures = events.filter((e) => {
        if (expectedSource && e.source !== expectedSource) return true;
        if (expectedTarget && e.target !== expectedTarget) return true;
        return false;
      });
      if (failures.length > 0) {
        addFail(`Role mismatch in ${failures.length} message(s)`);
      }
      break;
    }
    case 'TIMING_WITHIN': {
      const maxIntervalMs = Number(criterion.params.maxIntervalMs ?? Infinity);
      const timestamps = events
        .map((e) => new Date(e.timestamp).getTime())
        .filter((t) => !Number.isNaN(t))
        .sort((a, b) => a - b);
      for (let i = 1; i < timestamps.length; i++) {
        const interval = timestamps[i] - timestamps[i - 1];
        if (interval > maxIntervalMs) {
          addFail(`Timing interval ${interval}ms exceeds ${maxIntervalMs}ms`);
          break;
        }
      }
      break;
    }
    case 'NO_MISSING_MESSAGE': {
      // Placeholder: requires expected sequence detection
      break;
    }
    case 'CUSTOM_EXPRESSION': {
      const expression = String(criterion.params.expression ?? '');
      if (expression === 'NO_DUPLICATE_SEQUENCE') {
        const byType = new Map<number, Set<number>>();
        const duplicates = events.filter((e) => {
          if (e.sequenceNumber === undefined || e.sequenceNumber === null) return false;
          const set = byType.get(e.messageType) ?? new Set<number>();
          if (set.has(e.sequenceNumber)) return true;
          set.add(e.sequenceNumber);
          byType.set(e.messageType, set);
          return false;
        });
        if (duplicates.length > 0) {
          addFail(`Duplicate sequence numbers detected in ${duplicates.length} message(s)`);
        }
      } else if (expression === 'RANDOM_COORDINATE_ATOMICITY') {
        const invalid = events.filter((e) => {
          const fields = e.payload ?? e.fields ?? {};
          const lat = fields.latitude;
          const lon = fields.longitude;
          if (typeof lat !== 'number' || typeof lon !== 'number') return true;
          if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return true;
          if (lat === 37.389 && lon === 126.647) return true;
          return false;
        });
        if (invalid.length > 0) {
          addFail('Coordinate pair is not atomic or not within valid range');
        }
      }
      break;
    }
    default:
      break;
  }

  // Analyze analysis results for additional FAIL/WARN evidence
  const relevantAnalyses = analyses.filter((a) => events.some((e) => e.id === a.messageEventId));
  relevantAnalyses.forEach((a) => {
    a.fieldResults.forEach((issue) => {
      if (issue.severity === 'FAIL' || issue.severity === 'WARN') {
        evidence.push({
          messageType: a.messageType,
          field: issue.path,
          issue: issue.message,
        });
      }
    });
    a.roleResults.forEach((issue) => {
      if (issue.severity === 'FAIL' || issue.severity === 'WARN') {
        evidence.push({
          messageType: a.messageType,
          issue: issue.message,
        });
      }
    });
  });

  return { status, evidence };
};

export const evaluateTestCase = (
  testCase: TestCase,
  run: SimulationRun,
  events: MessageEvent[],
  analyses: MessageAnalysisResult[] = []
): TestCaseVerdict => {
  const allEvidence: TestVerdictEvidence[] = [];
  let overallStatus: TestVerdictStatus = 'PASS';

  testCase.passCriteria.forEach((criterion) => {
    const result = evaluateCriterion(criterion, testCase, run, events, analyses);
    allEvidence.push(...result.evidence);
    if (result.status === 'FAIL') {
      overallStatus = 'FAIL';
    }
  });

  if (overallStatus === 'PASS' && allEvidence.length > 0) {
    overallStatus = 'INCONCLUSIVE';
  }

  return {
    testCaseId: testCase.id,
    testCaseName: testCase.name,
    status: overallStatus,
    evidence: allEvidence,
    durationMs: run.durationMs,
    generatedCount: run.generatedCount,
    passCount: run.passCount,
    warnCount: run.warnCount,
    failCount: run.failCount,
  };
};

export const TestCaseVerdictService = {
  evaluateTestCase,
};

export default TestCaseVerdictService;
